import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../api/client';
import { useSSEStore } from '../../stores/sseStore';
import { useUIStore } from '../../stores/uiStore';
import type { UnifiedOrder, OrderFilters, OrdersResponse } from './types';

const DEFAULT_LIMIT = 50;

const DEFAULT_FILTERS: OrderFilters = {
  status: '',
  site: '',
  search: '',
  dateFrom: '',
  dateTo: '',
  pendingOnly: false,
  claimsOnly: false,
  notDeliveredOnly: false,
  notDeliveredAll: false,
  delayedOnly: false,
  deliveryIssuesOnly: false,
  readyToShipOnly: false,
  unreadOnly: false,
  staleReleasedOnly: false,
  whStatus: '',
  shipSubstatus: '',
};

interface OrdersSummary {
  total_orders: number;
  pending_purchases: number;
  total_revenue: number;
  total_profit: number;
  open_claims: number;
  not_delivered_count: number;
  delayed_count: number;
  delivery_issues_count: number;
  ready_to_ship_count: number;
  stale_released_count: number;
  unread_messages_count: number;
}

/** Build the query string shared by the list fetch and the CSV export. */
export function buildOrdersQueryParams(filters: OrderFilters, sellerId: number): URLSearchParams {
  const params = new URLSearchParams();
  params.set('seller_id', String(sellerId));
  if (filters.status) params.set('status', filters.status);
  if (filters.site) params.set('site', filters.site);
  if (filters.search) params.set('search', filters.search);
  if (filters.dateFrom) params.set('date_from', filters.dateFrom);
  if (filters.dateTo) params.set('date_to', filters.dateTo);
  if (filters.pendingOnly) params.set('pending_only', 'true');
  if (filters.claimsOnly) params.set('claims_only', 'true');
  if (filters.notDeliveredOnly) params.set('not_delivered', 'true');
  if (filters.notDeliveredAll) params.set('not_delivered_all', 'true');
  if (filters.delayedOnly) params.set('delayed', 'true');
  if (filters.deliveryIssuesOnly) params.set('delivery_issues', 'true');
  if (filters.readyToShipOnly) params.set('ship_status', 'ready_to_ship');
  if (filters.unreadOnly) params.set('unread_only', 'true');
  if (filters.staleReleasedOnly) params.set('stale_released_only', 'true');
  if (filters.whStatus) params.set('wh_status', filters.whStatus);
  if (filters.shipSubstatus) params.set('ship_substatus', filters.shipSubstatus);
  return params;
}

export function useOrders(externalPage?: number, externalPerPage?: number, setExternalTotal?: (n: number) => void) {
  const [orders, setOrders] = useState<UnifiedOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<OrderFilters>(DEFAULT_FILTERS);
  const page = externalPage ?? 1;
  const limit = externalPerPage ?? DEFAULT_LIMIT;
  const [summary, setSummary] = useState<OrdersSummary>({
    total_orders: 0,
    pending_purchases: 0,
    total_revenue: 0,
    total_profit: 0,
    open_claims: 0,
    not_delivered_count: 0,
    delayed_count: 0,
    delivery_issues_count: 0,
    ready_to_ship_count: 0,
    stale_released_count: 0,
    unread_messages_count: 0,
  });

  const fetchRef = useRef(0);
  const sellerId = useUIStore((s) => s.sellerId);
  const subscribe = useSSEStore((s) => s.subscribe);

  const fetchOrders = useCallback(async () => {
    const fetchId = ++fetchRef.current;
    setLoading(true);
    setError(null);

    const params = buildOrdersQueryParams(filters, sellerId);
    params.set('page', String(page));
    params.set('limit', String(limit));

    try {
      const res = await api.get<OrdersResponse['data']>(
        `/v3/orders/unified?${params.toString()}`
      );

      // Stale request protection
      if (fetchId !== fetchRef.current) return;

      if (res.success && res.data) {
        setOrders(res.data.orders);
        setExternalTotal?.(res.data.pagination.total);
        setSummary({ ...res.data.summary, ready_to_ship_count: res.data.summary.ready_to_ship_count || 0, stale_released_count: res.data.summary.stale_released_count || 0, unread_messages_count: res.data.summary.unread_messages_count || 0 });
      } else {
        setError(res.error || 'Failed to fetch orders');
      }
    } catch (err) {
      if (fetchId !== fetchRef.current) return;
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      if (fetchId === fetchRef.current) {
        setLoading(false);
      }
    }
  }, [sellerId, page, limit, filters, setExternalTotal]);

  // Fetch on filter/page/seller change
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // SSE subscriptions for real-time updates
  useEffect(() => {
    const events = [
      'order:updated',
      'shipment:updated',
      'purchase:updated',
      'purchase:created',
      'label:created',
      'claim:opened',
      'claim:updated',
      'warehouse:item_received',
      'warehouse:item_packed',
      'warehouse:item_closed',
    ];

    const unsubscribes = events.map((event) =>
      subscribe(event, () => {
        fetchOrders();
      })
    );

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [subscribe, fetchOrders]);

  const updateFilters = useCallback((partial: Partial<OrderFilters>) => {
    setFilters((prev) => ({ ...prev, ...partial }));
  }, []);

  const refresh = useCallback(() => {
    fetchOrders();
  }, [fetchOrders]);

  return {
    orders,
    loading,
    error,
    filters,
    updateFilters,
    summary,
    refresh,
  };
}
